import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "./SmoothScroll";
import { NAV, CABINET } from "@/lib/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onHero = pathname === "/" && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (pathname !== "/") navigate(`/#${id}`);
    else scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        onHero ? "bg-transparent border-b border-transparent" : "bg-[#FAF8F5]/85 backdrop-blur-xl border-b border-[#3D5A4C]/10"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-[1400px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <Link to="/" data-testid="header-logo-link" onClick={() => go("accueil")}>
          <Logo light={onHero} />
        </Link>
        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-link-${n.id}`} onClick={() => go(n.id)}
              className={`link-underline pb-1 text-[0.9rem] transition-colors ${onHero ? "text-[#FAF8F5]/90 hover:text-white" : "text-[#2c3b37] hover:text-[#3D5A4C]"}`}>
              {n.label}
            </button>
          ))}
          <a data-testid="header-rdv-button" href={CABINET.rdvUrl} target="_blank" rel="noopener noreferrer"
            className={`rounded-full px-5 py-2.5 text-[0.85rem] font-medium transition-colors duration-300 ${onHero ? "bg-[#FAF8F5] text-[#1C2826] hover:bg-[#E5DEC9]" : "bg-[#3D5A4C] text-[#FAF8F5] hover:bg-[#2E453A]"}`}>
            Prendre rendez-vous
          </a>
        </nav>
        <button data-testid="mobile-menu-toggle" aria-label="Menu" onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 ${onHero && !open ? "text-white" : "text-[#1C2826]"}`}>
          {open ? <X size={24} strokeWidth={1.3} /> : <Menu size={24} strokeWidth={1.3} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav data-testid="mobile-menu" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }} className="lg:hidden border-t border-[#3D5A4C]/10 bg-[#FAF8F5] px-6 pb-8 pt-4">
            {NAV.map((n, i) => (
              <button key={n.id} data-testid={`mobile-nav-link-${n.id}`} onClick={() => go(n.id)}
                className="flex w-full items-baseline gap-4 border-b border-[#3D5A4C]/10 py-4 text-left">
                <span className="font-serif-d text-sm italic text-[#B39158]">0{i + 1}</span>
                <span className="font-serif-d text-2xl text-[#1C2826]">{n.label}</span>
              </button>
            ))}
            <a data-testid="mobile-rdv-button" href={CABINET.rdvUrl} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
              className="mt-6 block w-full rounded-full bg-[#3D5A4C] py-3.5 text-center text-[#FAF8F5]">Prendre rendez-vous</a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
